import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateOrUpdateApplicationModel,
    CreateOrUpdateClientModel,
    GetApiResourcesByApplicationIdParams,
    GetApiResourcesByApplicationIdResponse,
    GetApplicationByIdParams,
    GetApplicationByIdResponse,
    GetApplicationDetailsByIdParams,
    GetApplicationDetailsByIdResponse,
    GetApplicationsParams,
    GetApplicationsResponse,
    GetClientDetailsByIdResponse,
    GetClientsByApplicationIdParams,
    GetClientsByApplicationIdResponse,
    ProblemDetails,
    UploadApplicationImageBody,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns a list of applications<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.read
 */
export const useGetApplicationsHook = () => {
    const getApplications = useCustomIDSClient<GetApplicationsResponse>();

    return useCallback(
        (params?: GetApplicationsParams, signal?: AbortSignal) => {
            return getApplications({ url: `/applications`, method: 'GET', params, signal });
        },
        [getApplications],
    );
};

export const getGetApplicationsQueryKey = (params?: GetApplicationsParams) => {
    return [`/applications`, ...(params ? [params] : [])] as const;
};

export const useGetApplicationsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApplicationsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApplicationsQueryKey(params);

    const getApplications = useGetApplicationsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>> = ({ signal }) =>
        getApplications(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApplicationsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>>;
export type GetApplicationsQueryError = ErrorType<ProblemDetails>;

export function useGetApplications<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetApplicationsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplications<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApplicationsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplications<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApplicationsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApplications<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApplicationsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApplicationsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.write
 */
export const useCreateApplicationHook = () => {
    const createApplication = useCustomIDSClient<GetApplicationByIdResponse>();

    return useCallback(
        (createOrUpdateApplicationModel: CreateOrUpdateApplicationModel, signal?: AbortSignal) => {
            return createApplication({
                url: `/applications`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApplicationModel,
                signal,
            });
        },
        [createApplication],
    );
};

export const useCreateApplicationMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
        TError,
        { data: CreateOrUpdateApplicationModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
    TError,
    { data: CreateOrUpdateApplicationModel },
    TContext
> => {
    const mutationKey = ['createApplication'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createApplication = useCreateApplicationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
        { data: CreateOrUpdateApplicationModel }
    > = (props) => {
        const { data } = props ?? {};

        return createApplication(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateApplicationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>
>;
export type CreateApplicationMutationBody = CreateOrUpdateApplicationModel;
export type CreateApplicationMutationError = ErrorType<ProblemDetails>;

export const useCreateApplication = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
            TError,
            { data: CreateOrUpdateApplicationModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
    TError,
    { data: CreateOrUpdateApplicationModel },
    TContext
> => {
    return useMutation(useCreateApplicationMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of a application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.read
 */
export const useGetApplicationByIdHook = () => {
    const getApplicationById = useCustomIDSClient<GetApplicationByIdResponse>();

    return useCallback(
        (id: string, params?: GetApplicationByIdParams, signal?: AbortSignal) => {
            return getApplicationById({ url: `/applications/${id}`, method: 'GET', params, signal });
        },
        [getApplicationById],
    );
};

export const getGetApplicationByIdQueryKey = (id: string, params?: GetApplicationByIdParams) => {
    return [`/applications/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetApplicationByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApplicationByIdQueryKey(id, params);

    const getApplicationById = useGetApplicationByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>> = ({ signal }) =>
        getApplicationById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApplicationByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>
>;
export type GetApplicationByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApplicationById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApplicationByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplicationById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplicationById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApplicationById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApplicationByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.write
 */
export const useUpdateApplicationHook = () => {
    const updateApplication = useCustomIDSClient<GetApplicationByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateApplicationModel: CreateOrUpdateApplicationModel, signal?: AbortSignal) => {
            return updateApplication({
                url: `/applications/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApplicationModel,
                signal,
            });
        },
        [updateApplication],
    );
};

export const useUpdateApplicationMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
        TError,
        { id: string; data: CreateOrUpdateApplicationModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApplicationModel },
    TContext
> => {
    const mutationKey = ['updateApplication'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateApplication = useUpdateApplicationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
        { id: string; data: CreateOrUpdateApplicationModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateApplication(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateApplicationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>
>;
export type UpdateApplicationMutationBody = CreateOrUpdateApplicationModel;
export type UpdateApplicationMutationError = ErrorType<ProblemDetails>;

export const useUpdateApplication = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
            TError,
            { id: string; data: CreateOrUpdateApplicationModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApplicationModel },
    TContext
> => {
    return useMutation(useUpdateApplicationMutationOptions(options), queryClient);
};
/**
 * Deletes an existing application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.write
 */
export const useDeleteApplicationHook = () => {
    const deleteApplication = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteApplication({ url: `/applications/${id}`, method: 'DELETE', signal });
        },
        [deleteApplication],
    );
};

export const useDeleteApplicationMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteApplication'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteApplication = useDeleteApplicationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteApplication(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteApplicationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>
>;

export type DeleteApplicationMutationError = ErrorType<ProblemDetails>;

export const useDeleteApplication = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteApplicationMutationOptions(options), queryClient);
};
/**
 * Get API Resources by application Id<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourcesByApplicationIdHook = () => {
    const getApiResourcesByApplicationId = useCustomIDSClient<GetApiResourcesByApplicationIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourcesByApplicationIdParams, signal?: AbortSignal) => {
            return getApiResourcesByApplicationId({
                url: `/applications/${id}/api-resources`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getApiResourcesByApplicationId],
    );
};

export const getGetApiResourcesByApplicationIdQueryKey = (
    id: string,
    params?: GetApiResourcesByApplicationIdParams,
) => {
    return [`/applications/${id}/api-resources`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourcesByApplicationIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourcesByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                TError,
                TData
            >
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourcesByApplicationIdQueryKey(id, params);

    const getApiResourcesByApplicationId = useGetApiResourcesByApplicationIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>> = ({
        signal,
    }) => getApiResourcesByApplicationId(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourcesByApplicationIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>
>;
export type GetApiResourcesByApplicationIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourcesByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourcesByApplicationIdParams,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourcesByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourcesByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                TError,
                TData
            >
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourcesByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourcesByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourcesByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourcesByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetApiResourcesByApplicationIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourcesByApplicationIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Requests the creation of a new client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useCreateClientHook = () => {
    const createClient = useCustomIDSClient<GetClientDetailsByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateClientModel: CreateOrUpdateClientModel, signal?: AbortSignal) => {
            return createClient({
                url: `/applications/${id}/clients`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateClientModel,
                signal,
            });
        },
        [createClient],
    );
};

export const useCreateClientMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
        TError,
        { id: string; data: CreateOrUpdateClientModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
    TError,
    { id: string; data: CreateOrUpdateClientModel },
    TContext
> => {
    const mutationKey = ['createClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createClient = useCreateClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
        { id: string; data: CreateOrUpdateClientModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return createClient(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>>;
export type CreateClientMutationBody = CreateOrUpdateClientModel;
export type CreateClientMutationError = ErrorType<ProblemDetails>;

export const useCreateClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
            TError,
            { id: string; data: CreateOrUpdateClientModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
    TError,
    { id: string; data: CreateOrUpdateClientModel },
    TContext
> => {
    return useMutation(useCreateClientMutationOptions(options), queryClient);
};
/**
 * Get Clients by application Id<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientsByApplicationIdHook = () => {
    const getClientsByApplicationId = useCustomIDSClient<GetClientsByApplicationIdResponse>();

    return useCallback(
        (id: string, params?: GetClientsByApplicationIdParams, signal?: AbortSignal) => {
            return getClientsByApplicationId({ url: `/applications/${id}/clients`, method: 'GET', params, signal });
        },
        [getClientsByApplicationId],
    );
};

export const getGetClientsByApplicationIdQueryKey = (id: string, params?: GetClientsByApplicationIdParams) => {
    return [`/applications/${id}/clients`, ...(params ? [params] : [])] as const;
};

export const useGetClientsByApplicationIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientsByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientsByApplicationIdQueryKey(id, params);

    const getClientsByApplicationId = useGetClientsByApplicationIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>> = ({
        signal,
    }) => getClientsByApplicationId(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientsByApplicationIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>
>;
export type GetClientsByApplicationIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientsByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetClientsByApplicationIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientsByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientsByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientsByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientsByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientsByApplicationId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientsByApplicationIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsByApplicationIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientsByApplicationIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the details of a application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.read
 */
export const useGetApplicationDetailsByIdHook = () => {
    const getApplicationDetailsById = useCustomIDSClient<GetApplicationDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetApplicationDetailsByIdParams, signal?: AbortSignal) => {
            return getApplicationDetailsById({ url: `/applications/${id}/detail`, method: 'GET', params, signal });
        },
        [getApplicationDetailsById],
    );
};

export const getGetApplicationDetailsByIdQueryKey = (id: string, params?: GetApplicationDetailsByIdParams) => {
    return [`/applications/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetApplicationDetailsByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApplicationDetailsByIdQueryKey(id, params);

    const getApplicationDetailsById = useGetApplicationDetailsByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>> = ({
        signal,
    }) => getApplicationDetailsById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApplicationDetailsByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>
>;
export type GetApplicationDetailsByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApplicationDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApplicationDetailsByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplicationDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApplicationDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApplicationDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApplicationDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApplicationDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApplicationDetailsByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Uploads an application image for an existing application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/applications.write
 */
export const useUploadApplicationImageHook = () => {
    const uploadApplicationImage = useCustomIDSClient<void>();

    return useCallback(
        (id: string, uploadApplicationImageBody: UploadApplicationImageBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadApplicationImageBody.File !== undefined) {
                formData.append(`File`, uploadApplicationImageBody.File);
            }

            return uploadApplicationImage({ url: `/applications/${id}/image`, method: 'PUT', data: formData, signal });
        },
        [uploadApplicationImage],
    );
};

export const useUploadApplicationImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>,
        TError,
        { id: string; data: UploadApplicationImageBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>,
    TError,
    { id: string; data: UploadApplicationImageBody },
    TContext
> => {
    const mutationKey = ['uploadApplicationImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadApplicationImage = useUploadApplicationImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>,
        { id: string; data: UploadApplicationImageBody }
    > = (props) => {
        const { id, data } = props ?? {};

        return uploadApplicationImage(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadApplicationImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>
>;
export type UploadApplicationImageMutationBody = UploadApplicationImageBody;
export type UploadApplicationImageMutationError = ErrorType<ProblemDetails>;

export const useUploadApplicationImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>,
            TError,
            { id: string; data: UploadApplicationImageBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadApplicationImageHook>>>,
    TError,
    { id: string; data: UploadApplicationImageBody },
    TContext
> => {
    return useMutation(useUploadApplicationImageMutationOptions(options), queryClient);
};
